import { Link } from "react-router-dom";
import PageHeader from "@/components/feature/PageHeader";
import { siteInfo } from "@/mocks/site";
import { useSeo } from "@/hooks/useSeo";

const scopeItems = [
  { icon: "ri-film-line", title: "影视作品资料", desc: "整理天海翼参演的电视剧、电影与纪实作品，包含作品类型、年份、主创团队与剧情简介。" },
  { icon: "ri-user-star-line", title: "人物与作品脉络", desc: "梳理不同时期作品的风格与角色，帮助影迷更清晰地理解她的表演历程。" },
  { icon: "ri-newspaper-line", title: "资讯与动态", desc: "汇集新闻、深度采访与活动现场报道，持续更新最新动态。" },
  { icon: "ri-search-eye-line", title: "站内检索与关联", desc: "支持关键词检索与作品、资讯之间的互相关联，方便深入探索。" },
];

export default function AboutPage() {
  useSeo({
    title: "关于本站 — 天海翼影视内容站",
    description:
      "关于天海翼影视内容站：了解本站的定位、内容范围、版权与免责声明，以及联系方式。这里是天海翼影视作品与明星资讯的整理与展示站点。",
    keywords: "关于天海翼影视内容站,天海翼资料,网站介绍,免责声明",
  });

  return (
    <>
      <PageHeader
        title="关于本站"
        description="天海翼影视内容站是一个面向影迷与影视爱好者的资讯整理站点，专注呈现天海翼的影视作品与相关动态。"
        crumbs={[{ label: "首页", path: "/" }, { label: "关于本站" }]}
      />

      <section className="mx-auto max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              <span className="h-5 w-1 rounded-full bg-accent-500" />
              网站定位
            </h2>
            <div className="mt-5 space-y-4 text-sm leading-7 text-foreground-700 md:text-base md:leading-8">
              <p>
                {siteInfo.brand} 聚焦于天海翼的影视作品介绍、明星资料与资讯动态。我们希望通过系统、清晰的内容整理，
                为影迷与影视内容爱好者提供一个便于查询、浏览与探索的信息入口。
              </p>
              <p>
                本站内容以作品资料与公开资讯为主，目标用户为天海翼的粉丝、影视内容爱好者，以及希望深入了解其作品背景的观众。
                我们将持续更新最新动态，同时保留经典作品的完整资料。
              </p>
              <p>
                需要特别说明的是：本站为影视资讯演示站点，不含任何播放、下载、会员或付费功能，
                所有作品与资讯资料仅供影迷了解作品内容使用。
              </p>
            </div>

            <h2 className="mt-12 flex items-center gap-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              <span className="h-5 w-1 rounded-full bg-accent-500" />
              内容范围
            </h2>
            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {scopeItems.map((item) => (
                <div key={item.title} className="rounded-lg border border-background-200 bg-background-100 p-5">
                  <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-md bg-secondary-100">
                    <i className={`${item.icon} text-xl text-secondary-700`} />
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-950">{item.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                </div>
              ))}
            </div>

            <h2 className="mt-12 flex items-center gap-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              <span className="h-5 w-1 rounded-full bg-accent-500" />
              版权与免责声明
            </h2>
            <div className="mt-5 space-y-4 rounded-lg border border-background-200 bg-background-100 p-6 text-sm leading-7 text-foreground-700">
              <p>
                本站所展示的作品名称、剧情简介、资讯内容等，均为内容整理与演示用途。相关权利归各自权利人所有，
                本站不主张任何权利，也未获得任何商业授权。
              </p>
              <p>
                若相关内容涉及版权问题，请通过下方联系方式与我们取得联系，我们将及时处理。
              </p>
              <p>
                本站不含任何影音播放、资源下载、会员订阅或在线支付功能，任何以本站名义提供的此类服务均与本站无关。
              </p>
            </div>
          </div>

          <aside className="lg:col-span-1">
            <div className="rounded-lg border border-background-200 bg-background-100 p-6 lg:sticky lg:top-24">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-mail-send-line text-accent-500" />
                联系方式
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                如对本站内容有建议、纠错或版权相关事宜，欢迎通过邮件与我们联系。
              </p>
              <a
                href={`mailto:${siteInfo.email}`}
                className="mt-4 flex cursor-pointer items-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-mail-line" />
                {siteInfo.email}
              </a>
              <div className="mt-6 space-y-3 border-t border-background-200 pt-5 text-sm text-foreground-600">
                <p className="flex items-center gap-2">
                  <i className="ri-map-pin-line text-foreground-400" />
                  {siteInfo.location}
                </p>
                <p className="flex items-center gap-2">
                  <i className="ri-time-line text-foreground-400" />
                  内容持续更新中
                </p>
              </div>
              <div className="mt-6 flex flex-col gap-2">
                <Link
                  to="/works"
                  className="flex cursor-pointer items-center justify-between rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  浏览影视作品
                  <i className="ri-arrow-right-line" />
                </Link>
                <Link
                  to="/news"
                  className="flex cursor-pointer items-center justify-between rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  查看最新资讯
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}